import React, { type RefObject, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { OverlayTrigger } from 'react-bootstrap'

import BootstrapTooltip from 'react-bootstrap/Tooltip'

import { useGetAllFormTemplatesQuery, useGetFilledOutFormTemplateFileMutation } from '../../../../../store/ai/formTemplate'
import { useLazyDownloadGlobalFileRequestQuery } from '../../../../../store/media/file'
import { useGetExternalUserByIdQuery } from '../../../../../store/externalUser/externalUser'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { selectMeetingSoleContact, selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { selectCurrentUser } from '../../../../../store/meetingSummary/user'
import { useAppSelector } from '../../../../../store/hooks'

import { useExportConfirmation } from '../../../../../components/ConfirmationDialog/useExportConfirmation'
import { useExportToPDF } from '../../../../../components/PDFExportButton/hooks'
import { DocumentConnectorActionButton, type DocumentConnectorActionItem } from '../../../../../components/DocumentConnector/DocumentConnectorActionButton'
import { useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import { Button } from '../../../../../components/Button/Button'

import { useOutsideClick } from '../../../../../common/useOutsideClick'
import { promptFileDownload } from '../../../../../common/fileUtils'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../../../../common/integrations'
import { formatDateYYYYMMDD } from '../../../../../common/dateUtils'
import { getConnectedDocumentServiceIds } from '../../../../../common/getConnectedDocumentServiceIds'
import { useLastUsedDocumentConnector } from '../../../../../common/lastUsedDocumentConnector'
import { TrackEvent, useTracking } from '../../common/tracking'

import { type FormTemplate } from '../../../../../models/FormTemplate'
import { type User } from '../../../../../models/User'
import { type AIAgentResult } from '../../../../../models/AIAgent'
import { type Scorecard } from '../../../../../models/Scorecard'
import { type AiFeature } from '../../../../../models/AiFeature'

import { aiAgentResultAnchor } from '../../../../../store/documentConnectors/documentConnectors'
import { SaveToDocumentConnectorModal } from '../Header/SaveToDocumentConnectorModal'
import { DocumentExportType } from '../../../../../components/DocumentConnector/models'
import { useMeetingLinkedFolder } from '../Header/useMeetingLinkedFolder'
import { escapeFormResultCsvValue, mapFormResultValueToString } from './utils'

const PDF_STYLES = `
  .disabled-contact-information-field {
   background: #fff !important;
 }`

const _FORM_RESULT_EXPORT_SELECT_OPTIONS = [
  {
    id: 'pdf',
    label: 'Export as PDF',
    icon: 'bi-file-earmark-pdf',
  },
  {
    id: 'csv',
    label: 'Export as CSV',
    icon: 'bi-file-earmark-spreadsheet',
  },
]

interface FormResultExportButtonProps {
  scorecard: Scorecard
  aiFeature: AiFeature
  formResult: AIAgentResult
  formContentRef: RefObject<HTMLElement>
  exportEventDetails: Record<string, unknown>
}

export const FormResultExportButton: React.FC<FormResultExportButtonProps> = ({ scorecard, aiFeature, formResult, formContentRef, exportEventDetails }) => {
  const accountId = useAppSelector(selectAccountId)
  const roomSession = useAppSelector(selectRoomSession)
  const currentUser = useAppSelector(selectCurrentUser)

  const { tracked } = useTracking()
  const { hasQuota } = useZocksQuota()

  const documentSummaryEnabled = hasQuota('ai.documentSummaryAgent.value')

  const { data: formTemplatesResponse } = useGetAllFormTemplatesQuery({ accountId, scorecardId: scorecard.id }, { skip: !documentSummaryEnabled })

  const [triggerGetFilledOutFormTemplateFile] = useGetFilledOutFormTemplateFileMutation()
  const [triggerGetFileDownloadInfo] = useLazyDownloadGlobalFileRequestQuery()

  const pdfName = `${aiFeature.name}${roomSession?.startedAt ? `_${formatDateYYYYMMDD(roomSession.startedAt)}` : ''}.pdf`
  const { exportToPDF: _handleExportToPdf, generatePDFBlob } = useExportToPDF(formContentRef, PDF_STYLES, pdfName)
  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation({ exportType: 'document' })

  const containerRef = useRef<HTMLDivElement>(null)

  const [formTemplateFillLoading, setFormTemplateFillLoading] = useState<boolean>(false)
  const [showDropdown, setShowDropdown] = useState<boolean>(false)
  const [activeServiceId, setActiveServiceId] = useState<IntegrationServiceId | null>(null)
  const soleContact = useAppSelector(selectMeetingSoleContact)
  const { data: soleContactData } = useGetExternalUserByIdQuery({ accountId, userId: soleContact?.id ?? '' }, { skip: !accountId || !soleContact })
  const [updatedContact, setUpdatedContact] = useState<User | null>(null)
  const contact = updatedContact ?? soleContactData?.data ?? null

  const connectedDocumentServiceIds = getConnectedDocumentServiceIds(currentUser)
  const { folderIds: initialFolderIds, isLoading: linkedFoldersLoading } = useMeetingLinkedFolder(
    activeServiceId ?? undefined,
    roomSession?.roomId,
    roomSession?.id,
  )

  const anchor = formResult?.id ? aiAgentResultAnchor(formResult.id) : null

  useOutsideClick(containerRef, () => {
    setShowDropdown(false)
  })

  const _handleExportToCsv = () => {
    const csvRows = Object.entries(formResult.result).map(([key, value]) => [
      escapeFormResultCsvValue(scorecard.descriptor.properties[key]?.title ?? key),
      escapeFormResultCsvValue(mapFormResultValueToString(value)),
    ])

    const csvContent = csvRows.map((row) => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `Form.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const _handleFillOutFormTemplate = async (formTemplateId: string) => {
    setShowDropdown(false)
    setFormTemplateFillLoading(true)
    try {
      const { id: formResultId } = formResult
      const { data: filledOutFormTemplateFile } = await triggerGetFilledOutFormTemplateFile({ accountId, formResultId, formTemplateId })
      if (!filledOutFormTemplateFile) {
        return null
      }

      const { fileId, fileName } = filledOutFormTemplateFile
      const { data: fileDownloadInfoResponse } = await triggerGetFileDownloadInfo({ accountId, fileId })
      if (!fileDownloadInfoResponse) {
        return
      }

      const { data: fileDownloadInfo } = fileDownloadInfoResponse
      const { location } = fileDownloadInfo

      const response = await fetch(location, { method: 'GET' })
      const fileContent = await response.blob()

      await promptFileDownload(fileContent, fileName)
    } catch (e) {
      console.error(e)
    } finally {
      setFormTemplateFillLoading(false)
    }
  }

  const handleExportToPdf = async () =>
    await runMutationWithConfirmation(tracked(_handleExportToPdf, { eventName: TrackEvent.EXPORT_TO_PDF, eventDetails: exportEventDetails }))

  const handleExportToCsv = async () =>
    await runMutationWithConfirmation(tracked(_handleExportToCsv, { eventName: TrackEvent.EXPORT_TO_CSV, eventDetails: exportEventDetails }))

  const handleFillOutFormTemplate = tracked(
    _handleFillOutFormTemplate,
    { eventName: TrackEvent.FORM_TEMPLATE_FILLED_OUT },
    {
      eventDetailsExtractor: (formTemplateId: string) => {
        return {
          formTemplateId,
        }
      },
    },
  )

  const handleStandardExportOptionSelected = async (optionId: string) => {
    setShowDropdown(false)

    switch (optionId) {
      case 'pdf':
        await handleExportToPdf()
        break
      case 'csv':
        await handleExportToCsv()
        break
    }
  }

  const handleServiceIdSelected = (serviceId: IntegrationServiceId) => {
    setShowDropdown(false)
    setActiveServiceId(serviceId)
  }

  const toggleDropdown = () => {
    setShowDropdown((prev) => !prev)
  }

  return (
    <>
      <ConfirmationModal />
      {activeServiceId ? (
        <SaveToDocumentConnectorModal
          serviceId={activeServiceId}
          show={true}
          onHide={() => setActiveServiceId(null)}
          generatePDFBlob={generatePDFBlob}
          defaultFileName={pdfName}
          initialFolderIds={initialFolderIds}
          initialFoldersReady={!linkedFoldersLoading}
          anchor={anchor}
          contact={contact}
          onContactUpdated={setUpdatedContact}
          exportType={DocumentExportType.FORM}
        />
      ) : null}
      <div className='position-relative'>
        <Button
          className='px-2 py-1 button-medium'
          variant='header'
          onClick={toggleDropdown}
          loading={formTemplateFillLoading}
          disabled={formTemplateFillLoading || accountIsLoading}
        >
          Export
          {showDropdown ? <i className='bi bi-chevron-contract' /> : <i className='bi bi-chevron-expand' />}
        </Button>
        {showDropdown ? (
          <div
            ref={containerRef}
            className='d-flex flex-column align-items-start gap-3 p-3 rounded-4 zocks-bg-color-white form-template-dropdown position-absolute'
          >
            <StandardFormResultExportOptions
              onOptionSelected={handleStandardExportOptionSelected}
              connectedDocumentServiceIds={connectedDocumentServiceIds}
              onServiceIdSelected={handleServiceIdSelected}
            />
            <FormTemplateFormResultExportOptions
              onOptionSelected={handleFillOutFormTemplate}
              formTemplates={formTemplatesResponse?.data ?? []}
              aiFeature={aiFeature}
              documentSummaryEnabled={documentSummaryEnabled}
            />
          </div>
        ) : null}
      </div>
    </>
  )
}

interface StandardFormResultExportOptionsProps {
  onOptionSelected: (optionId: string) => void
  connectedDocumentServiceIds: IntegrationServiceId[]
  onServiceIdSelected: (serviceId: IntegrationServiceId) => void
}

const StandardFormResultExportOptions: React.FC<StandardFormResultExportOptionsProps> = ({
  onOptionSelected,
  connectedDocumentServiceIds,
  onServiceIdSelected,
}) => {
  const [lastUsedServiceId, recordLastUsedServiceId] = useLastUsedDocumentConnector()

  const actionItems: DocumentConnectorActionItem[] = connectedDocumentServiceIds.map((serviceId) => ({
    key: serviceId,
    serviceId,
    label: `Save to ${getIntegrationDisplayName(serviceId)}`,
  }))

  const handleConnectorSelected = (key: string) => {
    const serviceId = key as IntegrationServiceId
    recordLastUsedServiceId(serviceId)
    onServiceIdSelected(serviceId)
  }

  return (
    <div className='d-flex flex-column gap-2'>
      <span className='lead-extra-small zocks-text-gray-700'>Standard exports</span>
      <div className='d-flex flex-column gap-2'>
        {_FORM_RESULT_EXPORT_SELECT_OPTIONS.map(({ id, label, icon }) => (
          <div
            key={`standard_form_result_export_option_${id}`}
            className='d-flex align-items-center gap-2 cursor-pointer py-2'
            onClick={() => onOptionSelected(id)}
          >
            <i className={`bi ${icon} fs-6`} />
            <span className='paragraph-medium bold'>{label}</span>
          </div>
        ))}
        <DocumentConnectorActionButton items={actionItems} primaryKey={lastUsedServiceId ?? undefined} onSelect={handleConnectorSelected} />
      </div>
    </div>
  )
}

interface FormTemplateFormResultExportOptionsProps {
  onOptionSelected: (formTemplateId: string) => void
  formTemplates: FormTemplate[]
  aiFeature: AiFeature
  documentSummaryEnabled: boolean
}

const FormTemplateFormResultExportOptions: React.FC<FormTemplateFormResultExportOptionsProps> = ({
  onOptionSelected,
  formTemplates,
  aiFeature,
  documentSummaryEnabled,
}) => {
  const navigate = useNavigate()

  const navigateToFormTemplateUpload = () => {
    navigate(`/workspace/setup/forms/form/details/${aiFeature.id}/templates/create`)
  }

  const navigateToFormTemplatesTab = () => {
    navigate(`/workspace/setup/forms/form/details/${aiFeature.id}/templates`)
  }

  if (!documentSummaryEnabled || !formTemplates.length) {
    return (
      <div className='d-flex flex-column align-items-center gap-3 px-2 py-3 zocks-bg-color-primary-light rounded-3'>
        <div className='d-flex flex-column align-items-center gap-1'>
          <span className='paragraph-medium bold'>Use your branded templates</span>
          <span className='paragraph-small text-center'>
            Go beyond basic PDFs. Upload your compliance-approved templates and Zocks fills them automatically with client data.
          </span>
        </div>
        {!documentSummaryEnabled ? (
          <Button variant='primary' className='button-small p-2' onClick={navigateToFormTemplatesTab}>
            Learn more
          </Button>
        ) : (
          <Button variant='primary' className='button-small p-2' onClick={navigateToFormTemplateUpload}>
            Upload your first template
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className='d-flex flex-column gap-2 align-self-stretch'>
      <div className='d-flex align-items-center justify-content-between align-self-stretch gap-3'>
        <span className='lead-extra-small zocks-text-gray-700'>AI Document fill</span>
        <div className='lead-extra-small d-flex align-items-center zocks-text-primary cursor-pointer' onClick={navigateToFormTemplateUpload}>
          <i className='bi bi-plus fs-6' />
          Add new
        </div>
      </div>
      <div className='d-flex flex-column'>
        {formTemplates.map(({ id, name }) => (
          <OverlayTrigger
            key={`form_template_form_result_export_option_${id}`}
            placement='top'
            overlay={<BootstrapTooltip>Download the filled template</BootstrapTooltip>}
          >
            <div className='d-flex align-items-center gap-2 cursor-pointer py-2' onClick={() => onOptionSelected(id)}>
              <i className='bi bi-clipboard-check fs-6' />
              <span className='paragraph-medium bold'>{name}</span>
            </div>
          </OverlayTrigger>
        ))}
      </div>
    </div>
  )
}
